/**
 * Lavelle Predictive Search
 */

class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.resultsContainer = this.querySelector('[data-predictive-search-results]');
    this.isOpen = false;

    this.setupEvents();
  }

  setupEvents() {
    if (!this.input) return;

    this.input.addEventListener('input', this.debounce((e) => {
      this.onChange(e);
    }, 300).bind(this));

    document.addEventListener('click', (e) => {
      if (!this.contains(e.target)) {
        this.close();
      }
    });
  }

  debounce(fn, wait) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  async onChange() {
    const query = this.input.value.trim();
    if (!query.length) {
      this.close();
      return;
    }

    try {
      const response = await fetch(`/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product,collection,page,article&resources[limit]=4`);
      const suggestions = await response.json();
      this.renderResults(suggestions, query);
    } catch (err) {
      console.error('Error fetching predictive search:', err);
    }
  }

  renderResults(data, query) {
    const products = data.resources?.results?.products || [];
    
    if (products.length === 0) {
      this.resultsContainer.innerHTML = `
        <div style="padding: 1.25rem; text-align: center; color: var(--color-text-muted);">
          No suggestions for "${this.escapeHtml(query)}"
        </div>
      `;
      this.open();
      return;
    }

    const html = `
      <div style="padding: 1rem 0;">
        <div style="padding: 0 1.25rem 0.5rem; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.08em; color: var(--color-gold); font-weight: 600;">Products</div>
        <div style="display: flex; flex-direction: column;">
          ${products.map(p => `
            <a href="${p.url}" style="display: flex; align-items: center; gap: 0.75rem; padding: 0.65rem 1.25rem; border-bottom: 1px solid var(--color-border); transition: background var(--transition-fast);" onmouseover="this.style.background='var(--color-lavender-light)'" onmouseout="this.style.background='transparent'">
              <img src="${p.featured_image?.url || ''}" alt="${this.escapeHtml(p.title)}" style="width: 44px; height: 56px; object-fit: cover; border-radius: var(--radius);">
              <div>
                <div style="font-weight: 500; font-size: 0.9rem; color: var(--color-primary);">${this.escapeHtml(p.title)}</div>
                <div style="font-size: 0.8rem; color: var(--color-text-muted);">${(p.price / 100).toFixed(2)}</div>
              </div>
            </a>
          `).join('')}
        </div>
        <div style="padding: 0.75rem 1.25rem 0.25rem; text-align: center;">
          <a href="/search?q=${encodeURIComponent(query)}" style="font-size: 0.8rem; text-transform: uppercase; letter-spacing: 0.05em; font-weight: 600; color: var(--color-primary); text-decoration: underline;">
            View all results for "${this.escapeHtml(query)}" &rarr;
          </a>
        </div>
      </div>
    `;

    this.resultsContainer.innerHTML = html;
    this.open();
  }

  open() {
    if (this.resultsContainer) {
      this.resultsContainer.style.display = 'block';
      this.isOpen = true;
    }
  }

  close() {
    if (this.resultsContainer) {
      this.resultsContainer.style.display = 'none';
      this.isOpen = false;
    }
  }

  escapeHtml(str) {
    return (str || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
}

customElements.define('predictive-search', PredictiveSearch);
